
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AdminStats {
  totalUsers: number;
  totalClubs: number;
  totalTeams: number;
  totalPlayers: number;
}

export const useAdminStats = () => {
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalClubs: 0,
    totalTeams: 0,
    totalPlayers: 0
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Fetch total users
      const { count: usersCount, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (usersError) throw usersError;

      // Fetch total clubs
      const { count: clubsCount, error: clubsError } = await supabase
        .from('clubs')
        .select('*', { count: 'exact', head: true });

      if (clubsError) throw clubsError;

      // Fetch total teams
      const { count: teamsCount, error: teamsError } = await supabase
        .from('teams')
        .select('*', { count: 'exact', head: true });

      if (teamsError) throw teamsError;

      // Fetch total players
      const { count: playersCount, error: playersError } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true });

      if (playersError) throw playersError;

      setStats({
        totalUsers: usersCount || 0,
        totalClubs: clubsCount || 0,
        totalTeams: teamsCount || 0,
        totalPlayers: playersCount || 0
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return {
    stats,
    loading,
    refetch: fetchStats
  };
};
